/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

// the color list position each asset of the History report keeps, by asset
// key, handed out the first time the asset is seen - by the map as it draws
// the report, or by the grid - so both draw an asset in the same color and
// it stays put as more assets load ...
let colorIndexes: Record<string, number> = {};

/**
 * Reads the color list position an asset of the History report keeps.
 *
 * ```typescript
 * import {getAssetHistoryColorIndex} from '@ion/dls/src/maps/helpers/assetHistoryColors';
 *
 * ...
 *
 * const index = getAssetHistoryColorIndex('768718');
 * ```
 *
 * @param assetKey - the asset's key, its asset id
 * @returns The position the asset keeps, `undefined` when it has none yet.
 */
export const getAssetHistoryColorIndex = (assetKey: string): number | undefined => (
  colorIndexes[assetKey]
);

/**
 * Gives each asset not seen before the next color list position, in the
 * order given - an asset already seen keeps its own.
 *
 * ```typescript
 * import {registerAssetHistoryColors} from '@ion/dls/src/maps/helpers/assetHistoryColors';
 *
 * ...
 *
 * registerAssetHistoryColors(Object.keys(assetsMap));
 * ```
 *
 * @param assetKeys - the asset keys, in the order they were drawn
 */
export const registerAssetHistoryColors = (assetKeys: string[]): void => {
  assetKeys.forEach(assetKey => {
    if (colorIndexes[assetKey] === undefined) {
      colorIndexes[assetKey] = Object.keys(colorIndexes).length;
    }
  });
};

/**
 * Starts the colors of a fresh draw of the History report. A redraw of the
 * same assets - the layers redrawn after Snap to Road is toggled - keeps the
 * colors already handed out; assets not all seen before are a new report,
 * whose colors start over.
 *
 * ```typescript
 * import {startAssetHistoryColors} from '@ion/dls/src/maps/helpers/assetHistoryColors';
 *
 * ...
 *
 * startAssetHistoryColors(Object.keys(assetsMap));
 * ```
 *
 * @param assetKeys - the asset keys the draw starts with
 */
export const startAssetHistoryColors = (assetKeys: string[]): void => {
  const isSameReport = assetKeys.length > 0 &&
    assetKeys.every(assetKey => colorIndexes[assetKey] !== undefined);
  if (!isSameReport) {
    colorIndexes = {};
  }
  registerAssetHistoryColors(assetKeys);
};

/**
 * Resolves the color an asset of the History report is drawn in - the one
 * the map draws it in, read off the same color list the map draws with. An
 * asset not seen before is given the next color.
 *
 * ```typescript
 * import {getAssetHistoryColor} from '@ion/dls/src/maps/helpers/assetHistoryColors';
 *
 * ...
 *
 * const color = getAssetHistoryColor('768718', Object.values(getHistoryColors()));
 * ```
 *
 * @param assetKey  - the asset's key, its asset id
 * @param colorList - the History color list the map draws with
 * @returns The asset's color, `undefined` when the list is empty.
 */
export const getAssetHistoryColor = (assetKey: string, colorList: string[]): string | undefined => {
  registerAssetHistoryColors([assetKey]);
  return colorList[colorIndexes[assetKey] % colorList.length];
};
